import React, { useEffect, useState } from 'react'
import { Box, Grid, Stack, Typography, Card, IconButton, List, ListItem, CircularProgress, Tooltip } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
// icons
import AngleRightIcon from "common/customIcons/angle_right";
// utils and helpers
import { formatTimestampToWithoutTime, formatTimeStampToDate } from "utilities/common";
import { getClosureFormsDetails } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from "components/MDButton";
import { BrandFilter, BoxFilter } from "common/filters";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from "components/tables/CommonAgGridTable";

const ClosureFormDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { distributionId } = useParams();
  const [searchParams] = useSearchParams();
  const { driver, region, subRegion, date } = Object.fromEntries(searchParams.entries());

  const { user } = useSelector(state => state.auth || {})

  const [details, setDetails] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const columnDefs = [
    {
      headerName: 'Product Name/SKU',
      field: 'name',
      suppressMenu: false,
      flex: 2,
      minWidth: 240,
      sortable: false,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { productName, sku, assets } = params?.data || {}
        const { thumbURL, name } = assets || {}

        return <Box style={{ display: "flex", gap: "10px" }}>
          <img src={thumbURL} alt={name} width={30} height={30} />
          <Box>
            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{productName}</Typography>
            {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px", padding: "0" }}>{sku}</Typography>}
          </Box>
        </Box>
      }
    },
    {
      headerName: 'Brand',
      field: 'brandName',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 180,
      sortable: false,
      valueGetter: ({ data }) => data?.brand?.name || "-"
    },
    {
      headerName: 'Weight',
      field: 'weight',
      suppressMenu: false,
      flex: 1,
      minWidth: 120,
      sortable: false,
      valueGetter: ({ data }) => data?.weight?.toString() ? `${data?.weight} g` : "-"
    },
    {
      headerName: 'Batch',
      field: 'batch',
      suppressMenu: false,
      flex: 1,
      minWidth: 130,
      sortable: false,
      cellRenderer: ({ data }) => {
        const batches = data?.batch || [];
        const firstFive = batches.slice(0, 2);
        const rest = batches.slice(2);

        return (
          <Box>
            {firstFive.map((batch, idx) => (
              <Typography
                key={batch?.batchNo + idx}
                variant='body2'
                style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
              >
                {batch?.sku} (Qty {batch?.assignedQty})
              </Typography>
            ))}
            {rest.length > 0 && (
              <Tooltip
                title={
                  <Box>
                    {rest.map((batch, idx) => (
                      <Typography
                        key={batch?.batchNo + idx}
                        variant='body2'
                        style={{ color: "#ffffff", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
                      >
                        {batch?.sku} (Qty {batch?.assignedQty})
                      </Typography>
                    ))}
                  </Box>
                }
                placement="top"
                arrow
              >
                <Typography
                  variant='body2'
                  style={{
                    color: "#2E2E3A",
                    fontWeight: "500",
                    fontSize: "12px",
                    padding: "0",
                    cursor: "pointer",
                    textDecoration: "underline",
                    lineHeight: "normal"
                  }}
                >
                  +{rest.length} more
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      headerName: 'Expected Quantity',
      field: 'expectedQty',
      suppressMenu: false,
      flex: 1,
      minWidth: 160,
      sortable: false,
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
    },
  ];

  const handleClosureFormDetails = async (filters = {}) => {
    try {
      setIsLoading(true);

      const request = { ...filters, distributionId, driverId: driver, regionId: region, subRegionId: subRegion };
      const formattedDate = formatTimeStampToDate(Number(date));
      if (formattedDate !== "Invalid date") {
        request.date = formattedDate;
      }
      const response = await dispatch(getClosureFormsDetails(request)).unwrap();
      const { data } = response || {};
      if (data) {
        setDetails(data || {});
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { value, name } = e.target;

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    handleClosureFormDetails({ ...customFilters, [name]: value });
  };

  const handleClearFilters = () => {
    setCustomFilters({});
    handleClosureFormDetails();
  };

  useEffect(() => {
    if (distributionId && driver) {
      handleClosureFormDetails();
    }
  }, [distributionId, driver])

  return (
    <CommonLayout>
      <Box p={4} className="cus-contents">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Closure Form ({details?.summary?.date ? formatTimestampToWithoutTime(details?.summary?.date || "") : ""})
            </Typography>
          </Box>
        </Stack>

        {/* Top section */}
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2.5, boxShadow: "none" }}>
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2}>
            {/* Top details */}
            <Grid item xs={12} sm={9}>
              <Typography variant='h5' fontWeight="semibold" style={{ fontSize: "20px" }}>Closure Form Details</Typography>
              <List sx={{ display: "flex", gap: 3, mt: 0.5 }}>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Driver:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.summary?.driverName}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Region:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.summary?.region}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Sub Region Name:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.summary?.subRegion}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Kit ID:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.summary?.kitId}</Typography>
                </ListItem>
              </List>
            </Grid>

            {/* Action buttons */}
            {details?.summary?.closureStatus && details?.summary?.closureStatus !== "Closed" && details?.summary?.closureStatus !== "approved" && <Grid item xs={12} sm={3}>
              <Stack direction='row' gap={1} alignItems='center' justifyContent="end">
                <div className="d-flex align-item-top gap-2">
                  <MDButton
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: "160px", boxShadow: "none !important" }}
                    onClick={() => {
                      navigate(`/hyperwolf/closure-forms/revenue-reconciliation/${distributionId}?driver=${driver}&region=${region}&subRegion=${subRegion}&date=${date}&forceClose=true`)
                    }}
                  >
                    Close Form
                  </MDButton>
                  {
                    details?.summary?.isDriverClosureSubmitted ?
                      <MDButton
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: "160px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important" }}
                        onClick={() => {
                          navigate(`/hyperwolf/closure-forms/revenue-reconciliation/${distributionId}?driver=${driver}&region=${region}&subRegion=${subRegion}&date=${date}`)
                        }}
                      >
                        Next
                      </MDButton>
                      : <Tooltip arrow title={"Closure form has not been submitted by driver."}>
                        <span>
                          <MDButton
                            variant="contained"
                            color="primary"
                            sx={{ minWidth: "160px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important" }}
                            disabled={true}
                          >
                            Next
                          </MDButton>
                        </span>
                      </Tooltip>
                  }
                </div>
              </Stack>
            </Grid>}
          </Grid>
        </Card>

        <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none" }}>
          {/* Quantity data */}
          <Stack direction='row' gap={3} xs={12} alignItems='start' justifyContent="space-between">
            <Card sx={{ p: { xs: 2 }, mb: 3, borderRadius: 2.5, boxShadow: "none", height: "100%", border: "1px solid #F2F3F7", flex: "1" }}>
              <div className="d-flex gap-1">
                <div className="d-flex align-item-top flex-column gap-1 flex-1">
                  <Typography variant='body2' fontSize="12px" color="#656575">Driver Name</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.summary?.driverName}</Typography>
                </div>
                <div className="d-flex align-item-top flex-column gap-1 flex-1">
                  <Typography variant='body2' fontSize="12px" color="#656575">Closure</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.summary?.closure || user?.name}</Typography>
                </div>
                <div className="d-flex align-item-top flex-column gap-1 flex-1">
                  <Typography variant='body2' fontSize="12px" color="#656575">Total Qty</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.summary?.totalQty}</Typography>
                </div>
                <div className="d-flex align-item-top flex-column gap-1 flex-1">
                  <Typography variant='body2' fontSize="12px" color="#656575">Expected Qty</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.summary?.expectedQty}</Typography>
                </div>
                <div className="d-flex align-item-top flex-column gap-1 flex-1">
                  <Typography variant='body2' fontSize="12px" color="#656575">Sold Qty</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.summary?.soldQty}</Typography>
                </div>
                {/* <div className="d-flex align-item-top flex-column gap-1 flex-1">
                  <Typography variant='body2' fontSize="12px" color="#656575">Total Retail Value</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.summary?.totalInventoryValue?.toLocaleString()}</Typography>
                </div> */}
                <div className="d-flex align-item-top flex-column gap-1 flex-1">
                  <Typography variant='body2' fontSize="12px" color="#656575">{"Today's Revenue"}</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.summary?.todaysRevenue?.toLocaleString()}</Typography>
                </div>
              </div>
            </Card>
          </Stack>

          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
            {/* Filters */}
            <Grid item xs={12}>
              <Box className='cus-filter-sec filter-gray-bg' mb={1}>
                <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <BoxFilter
                      value={customFilters?.boxId}
                      handleChange={handleChange}
                      placeholder={"Box Type"}
                      selectAllLabel={"All Boxes"}
                    />
                  </Grid>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <BrandFilter
                      value={customFilters?.brandId}
                      handleChange={handleChange}
                      placeholder={"Brands"}
                      selectAllLabel={"All"}
                    />
                  </Grid>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <MDButton
                      sx={{ minWidth: "120px", fontWeight: "normal" }}
                      type="button"
                      variant="outlined"
                      color="dark"
                      onClick={handleClearFilters}
                      disabled={!Object.values(customFilters)?.filter(Boolean).some((v) => v)}
                    >
                      Clear All
                    </MDButton>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Products listing */}
            <Grid item xs={12} className="table-nodata-size table-border-gray">
              <div
                className="ag-theme-quartz"
                style={{ height: "58vh", width: "100%" }}
              >
                {
                  isLoading ?
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
                      <CircularProgress size={34} />
                    </div>
                    : <AgGridReact
                      className="ag-grid--table"
                      pagination={false}
                      rowData={details.products || []}
                      noRowsOverlayComponent={NoDataFound}
                      overlayLoadingTemplate={'<span class="table-loader"></span>'}
                      columnDefs={columnDefs}
                      rowModelType={"clientSide"}
                      headerHeight={36}
                      rowHeight={56}
                      rowSelection="multiple"
                      suppressRowClickSelection={true}
                      defaultColDef={{ ...defaultColDef }}
                    />
                }
              </div>
            </Grid>
          </Grid>
        </Card>
      </Box>
    </CommonLayout >
  )
}

export default ClosureFormDetails
